import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { apiGetAvailableCoupons } from '../../services/CouponService';
import { setDiscountCode, removeItem } from '../../store/basket/basketSlice';
import { isSpendGetFreeItemCoupon, isBXGYCoupon } from '../coupon';

/**
 * Hook to validate coupon requirements and handle coupon removal
 */
function useCouponValidation() {
  const dispatch = useDispatch();
  const basket = useSelector((state) => state.basket.basket);
  const currentStore = useSelector((state) => state.base.common.currentStore);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch applied coupon details
  useEffect(() => {
    const fetchAppliedCoupon = async () => {
      if (!basket.discountcode || !currentStore?.id) {
        setAppliedCoupon(null);
        return;
      }

      try {
        setLoading(true);
        const response = await apiGetAvailableCoupons(currentStore.id, 10);
        let couponData = null;

        if (Array.isArray(response)) {
          couponData = response;
        } else if (Array.isArray(response?.data?.data)) {
          couponData = response.data.data;
        } else if (Array.isArray(response?.data)) {
          couponData = response.data;
        }

        if (Array.isArray(couponData)) {
          const coupon = couponData.find((c) => c.code === basket.discountcode);
          setAppliedCoupon(coupon || null);
        }
      } catch (error) {
        console.error('Error fetching applied coupon:', error);
        setAppliedCoupon(null);
      } finally {
        setLoading(false);
      }
    };

    fetchAppliedCoupon();
  }, [basket.discountcode, currentStore?.id]);

  /**
   * Check if coupon requirements are still met
   * @param {number} newSubtotal - New subtotal after item change
   * @returns {boolean} - True if requirements are met
   */
  const checkCouponRequirements = (newSubtotal) => {
    if (!appliedCoupon) return true;

    // Check minimum price requirement
    if (appliedCoupon.minprice && newSubtotal < appliedCoupon.minprice) {
      return false;
    }

    return true;
  };

  /**
   * Remove coupon and its benefits (discount, free items, etc.)
   * Prices will be automatically recalculated by removeItem action
   */
  const removeCoupon = () => {
    if (!basket.discountcode) return;

    // Remove free items from Redux if coupon type is 0 (SpendGetFreeItem)
    // removeItem action will automatically recalculate prices
    if (isSpendGetFreeItemCoupon(appliedCoupon) && appliedCoupon?.freeitemid) {
      const freeItems = basket.items.filter(
        (item) => item.item.id === appliedCoupon.freeitemid
      );
      freeItems.forEach((freeItem) => {
        dispatch(removeItem(freeItem.basketId));
      });
    }

    // Remove BXGY items from Redux if coupon type is 2 (BXGY)
    if (isBXGYCoupon(appliedCoupon)) {
      const bxgyItems = basket.items.filter((item) => item.bxgyInfo);
      bxgyItems.forEach((bxgyItem) => {
        dispatch(removeItem(bxgyItem.basketId));
      });
    }

    // Clear discount code
    // Prices will be automatically recalculated by calculateBasketPrices in basketSlice
    dispatch(setDiscountCode(''));

    setAppliedCoupon(null);
  };

  return {
    appliedCoupon,
    loading,
    checkCouponRequirements,
    removeCoupon,
  };
}

export default useCouponValidation;
